import {StockItem, VatRates} from '../../models-old/datastructures';
import {DisableRuleSupplierActionEnum, IDisableStockItemRule, IEnableStockItemRule, IOnHandDisableRule} from '../../../features/settings-new/models';
import {NumberUtils} from './number-utils';
import {StockUtils} from './stock-utils';
import {ObjectUtils} from './object-utils';
import {UiUtils} from './ui-utils';
import {blankNGPReport, NGPReport} from '../../models-old/ngp-reports/ngp-report';
import {StockFunctions} from '../../functions-old/stock-functions';


/**
 * @class ReportUtils
 * Utilities for manipulating and displaying reports.

 * @member convertStockItemsToNGPReports Converts a StockItem to an NGPReport.
 * @member calculateDayDifferenceCreatedDateAndSuppDates Calculates the difference in days between the createdDate and suppUsedLastDate1/suppUsedLastDate2.
 * @member calculateGPValues Calculate the GP Values for an NGP Report with a Stock Item.
 */
export class ReportUtils {


  static convertStockItemsToNGPReports(stockItems: StockItem[], storeId: string, vatRates: VatRates): NGPReport[] {
    if (stockItems?.length < 1) {
      return [];
    }
    const reports: NGPReport[] = [];
    stockItems.forEach((stock: StockItem) => {
      const item: NGPReport = ObjectUtils.deleteNonObjectProperties<NGPReport, StockItem>(stock, blankNGPReport);
      const itemCalculated: NGPReport = ReportUtils.doReportCalculations(stock, item, storeId, vatRates);
      reports.push({
        ...itemCalculated,
        lineColour: UiUtils.convertLineColourCodeToClassString(item.lineColourCode, item.storeId, 'lc lc-'),
      });
    });
    return reports;
  }

  /**
   * Do report calculations on the NGP Report
   * Calculations done: [`createdDate`, `suppUsedLastDate1`, `suppUsedLastDate2`, `daysDiff`, `gpValues`, `costMiss`]
   * Functions used: [`calculateDayDifferenceCreatedDateAndSuppDates`, `calculateGPValues`]
   *
   * @param stockItem A stock item to be used to do calculations on an NGPReport
   * @param ngpReport The NGP Report to add the calculations to.
   * @returns The NGPReport with calculations.
   */
  static doReportCalculations(stockItem: StockItem, ngpReport: NGPReport, storeId: string, vatRates: VatRates): NGPReport {
    const createdDate = ngpReport?.created ? new Date(ngpReport.createdDate) : null;
    const suppUsedLastDate1 = ngpReport?.suppUsedLastDate1 ? new Date(ngpReport.suppUsedLastDate1) : null;
    const suppUsedLastDate2 = ngpReport?.suppUsedLastDate2 ? new Date(ngpReport.suppUsedLastDate2) : null;
    const daysDiff = this.calculateDayDifferenceCreatedDateAndSuppDates(createdDate, suppUsedLastDate1, suppUsedLastDate2);
    const gpValues = this.calculateGPValues(stockItem, ngpReport, 2, storeId, vatRates);
    const costMiss = Math.abs(Number(stockItem.suppUsedLastPrice1) - Number(stockItem.latestCost)) > 0.05;

    return {
      ...ngpReport,
      created: daysDiff.isCreated,
      createdDate,
      cratedHasSupp1: daysDiff.createdHasSupplier1,
      costMiss,
      daysDiff: daysDiff.diff,
      diffGP: gpValues.differenceGP,
      nominalGP: gpValues.nominalGP,
      prevCostPrice: gpValues.prevCostPrice,
      prevGP: gpValues.previousGP,
      suppUsedLastPrice2: gpValues.suppUsedLastPrice2,
      suppUsedLastPrice1: gpValues.suppUsedLastPrice1,
      suppUsedLastDate1,
      suppUsedLastDate2,
    };
  }

  /**
   * Calculates the difference in days between the createdDate and suppUsedLastDate1/suppUsedLastDate2
   * This will most likely always be used on NGP Reports and Stock Items.
   *
   * @param createdDate The created date for the Stock Item.
   * @param suppUsedLastDate1 The supplier last used date 1.
   * @param suppUsedLastDate2 The supplier last used date 2.
   * @returns an object of `{ diff: number, isCreated: boolean, createdHasSupplier1: boolean }`.
   */
  static calculateDayDifferenceCreatedDateAndSuppDates(createdDate: Date, suppUsedLastDate1: Date, suppUsedLastDate2: Date): {
    diff: number;
    isCreated: boolean;
    createdHasSupplier1: boolean;
  } {
    const timeCalculation: number = 1000 * 60 * 60 * 24;
    let daysDiff = 0;
    let isCreated = false;
    let createdHasSupplier1 = false;
    if (suppUsedLastDate1) {
      if (createdDate && (!suppUsedLastDate2 || createdDate.getTime() > suppUsedLastDate2.getTime())) {
        if (suppUsedLastDate1.getTime() >= createdDate.getTime()) {
          createdDate.setHours(0, 0, 0, 0);
          daysDiff = Math.round((suppUsedLastDate1.getTime() - createdDate.getTime())) / timeCalculation;
        } else {
          createdHasSupplier1 = true;
        }
        isCreated = true;
      } else if (suppUsedLastDate2) {
        daysDiff = Math.round((suppUsedLastDate1.getTime() - suppUsedLastDate2.getTime()) / timeCalculation);
      }
    }
    return {
      diff: daysDiff,
      isCreated,
      createdHasSupplier1,
    };
  }

  /**
   * Calculate the GP Values for an NGP Report with a Stock Item.
   * This will most likely always be used on NGP Reports and Stock Items.
   *
   * @param stockItem The stock item used to calculate the GP.
   * @param ngpReport The NGP Report used to calculate the GP.
   * @param decimalDigits To how many decimal points should the value be formatted.
   * @param storeId The id for the store.
   * @returns an object of `{ nominalGP: number, prevCostPrice: number, previousGP: number, differenceGP: number }`
   * Deprecated created calculateGPFromPRice to be used
   */
  static calculateGPValues(stockItem: StockItem, ngpReport: NGPReport, decimalDigits: number, storeId: string, vatRates: VatRates): {
    nominalGP: number;
    prevCostPrice: number;
    previousGP: number;
    differenceGP: number;
    suppUsedLastPrice1: number;
    suppUsedLastPrice2: number;
  } {
    const sULP1 = +stockItem.suppUsedLastPrice1;
    let sULP2 = +stockItem.suppUsedLastPrice2;
    if (sULP2 === 0) {
      sULP2 = +stockItem.latestCost;
    }


    if (typeof (stockItem.vatR) === 'boolean') {
      stockItem.vatR = 1;
    }
    const prevCostPrice = sULP2;

    const nominalGP = StockUtils.calculateGP(vatRates[stockItem.vatR]?.vatRate, stockItem.sellPriIncl1, stockItem.latestCost);

    const previousGP = sULP2 ? StockUtils.calculateGP(vatRates[stockItem.vatR].vatRate, stockItem.sellPriIncl1, sULP2) : 0;
    const differenceGP = sULP2
      ? ((ngpReport.sellPriExcl1 - sULP1) / ngpReport.sellPriExcl1 * 100 - (ngpReport.sellPriExcl1 - sULP2) / ngpReport.sellPriExcl1 * 100)
      : 0;

    const diffGP = nominalGP - previousGP;

    return {
      nominalGP: +NumberUtils.formatNumberToDecimals(nominalGP, decimalDigits),
      prevCostPrice: +NumberUtils.formatNumberToDecimals(prevCostPrice, decimalDigits),
      previousGP: +NumberUtils.formatNumberToDecimals(previousGP, decimalDigits),
      differenceGP: +NumberUtils.formatNumberToDecimals(diffGP, decimalDigits),
      suppUsedLastPrice1: +NumberUtils.formatNumberToDecimals(+stockItem.suppUsedLastPrice1, decimalDigits),
      suppUsedLastPrice2: +NumberUtils.formatNumberToDecimals(sULP2, decimalDigits),
    };
  }

  static calculatePriceFromGP(storeId: string, ngpReport: NGPReport | StockItem, vatRates: VatRates, newGP?: number): number {
    const vatR = (vatRates[ngpReport.vatR].vatRate / 100);
    const gp = ![undefined, null].includes(newGP) ? newGP : (
      ![undefined, null].includes(ngpReport.nominalGP) ? +ngpReport.nominalGP : +ngpReport.gp1
    );
    let clampedGP = Math.min(gp, 99.99);
    if (clampedGP === 0) {
      clampedGP = 0.01;
    }
    const x = -((100 * ngpReport.latestCost) / (+clampedGP - 100));
    const price = x * (1 + vatR);
    return Math.round(price * 100) / 100;
  }

  static determineIfItemIsDisabled(stockItem: StockItem, rules: IDisableStockItemRule): boolean {
    return StockFunctions.isDisabled(stockItem, rules);
  }

  static applyDescPrefix = (
    report: NGPReport | StockItem,
    rule: IOnHandDisableRule,
    enabledRule: IEnableStockItemRule,
    enable: boolean,
  ): void => {
    if (enable) {
      const prefixes = [
        ...(enabledRule?.descPrefixArray ?? []),
        ...(rule?.descPrefix ? [rule.descPrefix] : []),
      ].filter(Boolean).sort((a, b) => b.length - a.length);

      if (report.desc) report.desc = this.stripPrefixes(report.desc, prefixes);
    } else if (rule?.descPrefix) {
      const trimmed = report.desc?.trimStart();
      if (trimmed && !trimmed.startsWith(rule.descPrefix)) {
        report.desc = `${rule.descPrefix} ${trimmed}`;
      }
    }
  };

  static applyDescSuffix = (
    report: NGPReport | StockItem,
    rule: IOnHandDisableRule,
    enabledRule: IEnableStockItemRule,
    enable: boolean,
  ): void => {
    if (enable) {
      const suffixes = [
        ...(enabledRule?.descSuffixArray ?? []),
        ...(rule?.descSuffix ? [rule.descSuffix] : []),
      ].filter(Boolean).sort((a, b) => b.length - a.length);

      if (report.desc) report.desc = this.stripSuffixes(report.desc, suffixes);
    } else if (rule?.descSuffix) {
      const trimmed = report.desc?.trimEnd();
      if (trimmed && !trimmed.endsWith(rule.descSuffix)) {
        report.desc = `${trimmed} ${rule.descSuffix}`;
      }
    }
  };

  static applyOnHoldCode = (
    report: NGPReport | StockItem,
    rule: IOnHandDisableRule,
    enabledRule: IEnableStockItemRule,
    enable: boolean,
    defaults: { onHoldCode: number; lineColourCode: number } = {
      onHoldCode: Number(report.originalValue['onHoldCode'].value),
      lineColourCode: Number(report.originalValue['lineColourCode'].value),
    },
  ): void => {
    if (enable) {
      if (rule.onHoldCode !== undefined && rule.onHoldCode !== null) {
        if (!(report.originalValue['_tags'].value as string[]).includes('disabled')) {
          report.onHoldCode = defaults.onHoldCode;
        } else {
          report.onHoldCode = enabledRule?.onHoldCode ?? 0;
        }
      }
    } else {
      if (rule.onHoldCode !== undefined && rule.onHoldCode !== null) {
        report.onHoldCode = rule.onHoldCode;
      }
    }
  };

  static applyLineColour = (
    report: NGPReport | StockItem,
    rule: IOnHandDisableRule,
    enableRule: IEnableStockItemRule,
    enable: boolean,
    defaults: { onHoldCode: number; lineColourCode: number } = {
      onHoldCode: Number(report.originalValue['onHoldCode'].value),
      lineColourCode: Number(report.originalValue['lineColourCode'].value),
    },
  ): void => {
    if (enable) {
      if (rule.lineColour !== undefined && rule.lineColour !== null) {
        if (!(report.originalValue['_tags'].value as string[]).includes('disabled')) {
          report.lineColourCode = Number(defaults.lineColourCode);
        } else {
          report.lineColourCode = enableRule?.lineColour ? Number(enableRule.lineColour) : 0;
        }

      }
    } else {
      if (rule.lineColour !== undefined && rule.lineColour !== null) {
        report.lineColourCode = rule.lineColour;
      }
    }
  };

  static applyRegularSupplier = (
    report: NGPReport | StockItem,
    rule: IOnHandDisableRule,
    enable: boolean,
    defaults: { regularSuppCode: string } = {
      regularSuppCode: String(report.originalValue['regularSuppCode']?.value ?? ''),
    },
  ): void => {
    if (!rule.supplierAction || rule.supplierAction === DisableRuleSupplierActionEnum.none) {
      return;
    }

    if (enable) {
      report.regularSuppCode = defaults.regularSuppCode;
      return;
    }

    if (rule.supplierAction === DisableRuleSupplierActionEnum.remove) {
      report.regularSuppCode = '';
    } else if (rule.supplierAction === DisableRuleSupplierActionEnum.set && rule.regularSuppCode) {
      report.regularSuppCode = rule.regularSuppCode;
    }
  };

  static enableReport(
    report: NGPReport | StockItem,
    disableRules: IDisableStockItemRule,
    enabledRules: IEnableStockItemRule,
    enable: boolean,
    defaults: { onHoldCode: number; lineColourCode: number } = {
      onHoldCode: Number(report.originalValue['onHoldCode'].value),
      lineColourCode: Number(report.originalValue['lineColourCode'].value),
    },
  ): boolean {
    const apply = (rule: IOnHandDisableRule, enabledRule: IEnableStockItemRule): void => {
      this.applyDescPrefix(report, rule, enabledRule, enable);
      this.applyDescSuffix(report, rule, enabledRule, enable);
      this.applyOnHoldCode(report, rule, enabledRule, enable, defaults);
      this.applyLineColour(report, rule, enabledRule, enable, defaults);
      this.applyRegularSupplier(report, rule, enable);
    };

    if (enable) {
      for (const ruleKey of Object.keys(disableRules)) {
        apply(disableRules[ruleKey] as IOnHandDisableRule, enabledRules);
      }
      return true;
    } else {
      const onHandValue = Number(report.onHand ?? 0);
      if (onHandValue === 0) {
        if (disableRules.onHandZero) {
          apply(disableRules.onHandZero, enabledRules);
          return true;
        }
      } else if (disableRules.onHandNotZero) {
        apply(disableRules.onHandNotZero, enabledRules);
        return true;
      }
    }
    return false;
  }


  static stripPrefixes = (text: string, prefixes: string[]): string => {
    let changed: boolean;
    do {
      changed = false;
      const trimmed = text.trimStart();
      for (const p of prefixes) {
        if (trimmed.startsWith(p)) {
          text = trimmed.slice(p.length);
          changed = true;
          break;
        }
      }
    } while (changed);
    return text.trimStart();
  };

  static stripSuffixes = (text: string, suffixes: string[]): string => {
    let changed: boolean;
    do {
      changed = false;
      const trimmed = text.trimEnd();
      for (const s of suffixes) {
        if (trimmed.endsWith(s)) {
          text = trimmed.slice(0, trimmed.length - s.length);
          changed = true;
          break;
        }
      }
    } while (changed);
    return text.trimEnd();
  };

}
